import React, { useEffect, useState } from 'react'
import ProductsTable from '../components/ProductsTable'
import { useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { ColumnFiltersState } from '@tanstack/react-table'
import LicenseTable from '../components/LicenseTable'
import { dashboardLabel } from '../utilities/pageConstants'
import { commonLabelName, pathName } from '../utilities/commonConstants'
import CommercialLicenses from './studentLicenses/CommercialLicenses'
import StudentLicenses from './studentLicenses/StudentLicenses'
import CommercialTrainingCourses from './studentLicenses/CommercialTrainingCourses'
import AcademicTrainingCourses from './studentLicenses/AcademicTrainingCourses'
import EvaluationLicenses from './studentLicenses/EvaluationLicenses'

// Countries blocked from accessing Evaluation Licenses tab
const EVALUATION_BLOCKED_COUNTRIES: string[] = []

/**
 * Dashboard component that provides a tabbed interface for users to view product downloads and license information.
 * It also handles filtering based on URL parameters and user interactions.
 *
 * @component
 * @returns {JSX.Element} The rendered Dashboard component.
 */
const Dashboard: React.FC = () => {
    /**
     * Custom hook to parse URL query parameters.
     *
     * @returns {URLSearchParams} Parsed query parameters from the URL.
     */
    const useQueryFunction = () => {
        return new URLSearchParams(useLocation().search)
    }
    const location = useLocation()
    const navigate = useNavigate()
    const mode = location?.state?.mode

    const query = useQueryFunction()
    const name = query.get('name')
    const version = query.get('version')

    const country = localStorage.getItem('country')
    const isUsa = country === 'United States'
    const isEvaluationAllowed = isUsa && (country ? !EVALUATION_BLOCKED_COUNTRIES.includes(country) : false)
    const stateType = location.state?.type

    // Check if we're on the training-courses route
    const isTrainingRoute = location.pathname === '/training-courses'

    // Check if we're on the evaluation-licenses route
    const isEvaluationRoute = location.pathname === '/evaluation-licenses'

    // Determine user type from localStorage for training route
    const isStudentUser = localStorage.getItem('isStudentUser') === 'true'

    // Determine effective user type - use stateType or derive from localStorage
    const effectiveUserType = stateType || (isStudentUser ? commonLabelName?.academic : commonLabelName?.commercial)

    const [activeTab, setActiveTab] = useState(0)
    const [mainTab, setMainTab] = useState<'productDownloads' | 'myLicenses' | 'commercial' | 'academic' | 'training' | 'evaluation'>(
        'productDownloads'
    )
    // Key to force ProductsTable remount
    const [productsTableKey, setProductsTableKey] = useState(0)
    const [state, setState] = useState('Current')
    const [isMessageVisible, setIsMessageVisible] = useState(true)
    // const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isNormalUser, setIsNormalUser] = useState(false)
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState | null>(
        name && version && state === 'Current'
            ? [
                  { id: 'ItemName', value: name },
                  { id: 'ItemVersion', value: version }
              ]
            : name && version && state === 'Prior'
              ? [{ id: 'ItemName', value: name }]
              : []
    )

    const [selectedLicenses, setSelectedLicenses] = useState<Set<string>>(new Set())
    const [quantities, setQuantities] = useState<Record<string, number>>({})
    const [selectedLicensesDetails, setSelectedLicensesDetails] = useState<
        { ProductCode: string; ProductName: string; Quantity: number; Price: string; UiPrice: string; Term: string }[]
    >([])
    const [isModalOpen, setModalOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [loadingTabType, setLoadingTabType] = useState<'Current' | 'Prior' | null>(null)

    /**
     * Callback to update loading state from child components
     * @param {boolean} loading - Whether data is currently loading
     * @param {string} tabType - The tab type that is loading (for ProductsTable)
     */
    const handleLoadingChange = (loading: boolean, tabType?: string) => {
        setIsLoading(loading)
        if (tabType) {
            setLoadingTabType(loading ? (tabType as 'Current' | 'Prior') : null)
        } else {
            setLoadingTabType(null)
        }
    }

    /**
     * Handles tab navigation with proper URL routing
     * @param {string} tab - The tab to navigate to
     */
    const handleTabNavigation = (tab: 'productDownloads' | 'myLicenses' | 'commercial' | 'academic' | 'training' | 'evaluation') => {
        setMainTab(tab)
        if (tab === 'productDownloads') {
            setProductsTableKey((prev) => prev + 1)
        }

        // Only handle route changes for Training and Evaluation tabs
        if (tab === 'training' && !isTrainingRoute) {
            // Navigate to training-courses route
            let userType = null
            if (isStudentUser) {
                userType = commonLabelName?.academic
            } else {
                userType = commonLabelName?.commercial
            }

            navigate('/training-courses', {
                state: {
                    mode: 'training',
                    type: userType
                },
                replace: true
            })
        } else if (tab === 'evaluation' && !isEvaluationRoute) {
            // Navigate to evaluation-licenses route
            navigate('/evaluation-licenses', {
                state: {
                    mode: 'evaluation'
                },
                replace: true
            })
        } else if ((isTrainingRoute || isEvaluationRoute) && (tab === 'productDownloads' || tab === 'myLicenses')) {
            // Navigate back to dashboard when switching to productDownloads or myLicenses from training/evaluation routes
            // Preserve the current user type for proper tab display
            let userType = null
            if (isStudentUser) {
                userType = commonLabelName?.academic
            } else {
                userType = commonLabelName?.commercial
            }

            navigate('/', {
                state: {
                    type: userType,
                    requestedTab: tab // Pass the requested tab
                },
                replace: true
            })
        } else if ((isTrainingRoute || isEvaluationRoute) && (tab === 'commercial' || tab === 'academic')) {
            // Navigate back to dashboard when switching to commercial/academic tabs from training/evaluation routes
            let userType = null
            if (isStudentUser) {
                userType = commonLabelName?.academic
            } else {
                userType = commonLabelName?.commercial
            }

            navigate('/', {
                state: { type: userType },
                replace: true
            })
        }
        // For all other tabs when already on dashboard, just switch tabs without changing routes
    }

    /**
     * Handles switching between the "Current" and "Prior" tabs for product downloads.
     *
     * @param {number} tabIndex - The index of the tab (0 for "Current", 1 for "Prior").
     */
    const showTab = (tabIndex: number) => {
        setActiveTab(tabIndex)
        setState(tabIndex === 0 ? 'Current' : 'Prior')
    }

    useEffect(() => {
        if (mode === 'commercial' && location?.pathname === '/' && localStorage.getItem('isBlockedUser') === 'true') {
            setIsNormalUser(true)
        }
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        // Check if there's a requested tab from navigation state
        const requestedTab = location.state?.requestedTab

        if (requestedTab) {
            setMainTab(requestedTab)
        } else if (mode === 'training' || isTrainingRoute) {
            setMainTab('training')
        } else if (mode === 'evaluation' || isEvaluationRoute) {
            setMainTab('evaluation')
        } else if (effectiveUserType === commonLabelName?.academic) {
            setMainTab('academic')
        } else if (effectiveUserType === commonLabelName?.commercial) {
            setMainTab('commercial')
        } else {
            setMainTab('productDownloads')
        }
    }, [location.state, mode, isTrainingRoute, isEvaluationRoute, effectiveUserType])

    useEffect(() => {
        if (!isUsa) {
            setMainTab('productDownloads')
        }
        // eslint-disable-next-line
    }, [])

    /**
     * Effect hook that checks if the page was navigated to with a purchase state and updates the main tab accordingly.
     */
    useEffect(() => {
        if (location?.state?.isPurchase === true) {
            setMainTab('productDownloads')
            setSelectedLicenses(new Set())
            setQuantities({})
            setSelectedLicensesDetails([])
            setModalOpen(false)
            // Optionally, clear the flag so it doesn't trigger again
            window.history.replaceState({}, document.title)
        }
    }, [location?.state?.isPurchase])

    return (
        <>
            <div className='px-8 py-4 text-center bg-white'>
                <div className='flex items-center justify-start mb-8 space-x-4 border-b border-[#e1e1e1]'>
                    <button
                        onClick={() => handleTabNavigation('productDownloads')}
                        disabled={isLoading && mainTab !== 'productDownloads'}
                        className={`px-6 py-3 text-lg font-semibold ${
                            isLoading && mainTab !== 'productDownloads' ? 'cursor-not-allowed opacity-50' : ''
                        } ${mainTab === 'productDownloads' ? 'border-b-2 border-primary text-primary' : 'text-secondary'}`}
                    >
                        {dashboardLabel?.productDownloads}
                    </button>
                    <button
                        onClick={() => handleTabNavigation('myLicenses')}
                        disabled={isLoading && mainTab !== 'myLicenses'}
                        className={`px-6 py-3 text-lg font-semibold ${
                            isLoading && mainTab !== 'myLicenses' ? 'cursor-not-allowed opacity-50' : ''
                        } ${mainTab === 'myLicenses' ? 'border-b-2 border-primary text-primary' : 'text-secondary'}`}
                    >
                        {dashboardLabel?.myLicenses}
                    </button>
                    {/* Show Commercial tab if user is commercial type */}
                    {((effectiveUserType === commonLabelName?.commercial ||
                        (isTrainingRoute && !isStudentUser) ||
                        (isEvaluationRoute && !isStudentUser) ||
                        (mode === 'training' && !isStudentUser) ||
                        (mode === 'evaluation' && !isStudentUser)) &&
                        !isNormalUser &&
                        isStudentUser &&
                        isUsa) ||
                        (!isNormalUser && !isStudentUser && isUsa && (
                            <button
                                onClick={() => handleTabNavigation('commercial')}
                                disabled={isLoading && mainTab !== 'commercial'}
                                className={`px-6 py-3 text-lg font-semibold ${
                                    isLoading && mainTab !== 'commercial' ? 'cursor-not-allowed opacity-50' : ''
                                } ${mainTab === 'commercial' ? 'border-b-2 border-primary text-primary' : 'text-secondary'}`}
                            >
                                {dashboardLabel?.commercial}
                            </button>
                        ))}
                    {/* Show Academic tab if user is academic type */}
                    {(effectiveUserType === commonLabelName?.academic ||
                        (isTrainingRoute && isStudentUser) ||
                        (isEvaluationRoute && isStudentUser) ||
                        (mode === 'training' && isStudentUser) ||
                        (mode === 'evaluation' && isStudentUser)) &&
                        !isNormalUser &&
                        isStudentUser &&
                        isUsa && (
                            <button
                                onClick={() => handleTabNavigation('academic')}
                                disabled={isLoading && mainTab !== 'academic'}
                                className={`px-6 py-3 text-lg font-semibold ${
                                    isLoading && mainTab !== 'academic' ? 'cursor-not-allowed opacity-50' : ''
                                } ${mainTab === 'academic' ? 'border-b-2 border-primary text-primary' : 'text-secondary'}`}
                            >
                                {dashboardLabel?.academic}
                            </button>
                        )}
                    {/* Show Training tab */}
                    {!isNormalUser && isUsa && (
                        <button
                            onClick={() => handleTabNavigation('training')}
                            disabled={isLoading && mainTab !== 'training'}
                            className={`px-6 py-3 text-lg font-semibold ${
                                isLoading && mainTab !== 'training' ? 'cursor-not-allowed opacity-50' : ''
                            } ${mainTab === 'training' ? 'border-b-2 border-primary text-primary' : 'text-secondary'}`}
                        >
                            {dashboardLabel?.trainingCourses}
                        </button>
                    )}
                    {/* {(!isNormalUser &&
                        (effectiveUserType === commonLabelName?.academic ||
                            effectiveUserType === commonLabelName?.commercial ||
                            mode === 'training' ||
                            mode === 'evaluation' ||
                            isTrainingRoute ||
                            isEvaluationRoute) &&
                        !isNormalUser &&
                        isStudentUser) ||
                        (!isNormalUser && !isStudentUser && (
                            <button
                                onClick={() => handleTabNavigation('training')}
                                className={`px-6 py-3 text-lg font-semibold ${
                                    mainTab === 'training' ? 'border-b-2 border-primary text-primary' : 'text-secondary'
                                }`}
                            >
                                {dashboardLabel?.trainingCourses}
                            </button>
                        ))} */}
                    {/* Show Evaluation Licenses tab */}
                    {!isNormalUser && isEvaluationAllowed && (
                        <button
                            onClick={() => handleTabNavigation('evaluation')}
                            disabled={isLoading && mainTab !== 'evaluation'}
                            className={`px-6 py-3 text-lg font-semibold ${
                                isLoading && mainTab !== 'evaluation' ? 'cursor-not-allowed opacity-50' : ''
                            } ${mainTab === 'evaluation' ? 'border-b-2 border-primary text-primary' : 'text-secondary'}`}
                        >
                            Evaluation Licenses
                        </button>
                    )}
                </div>

                {mainTab === 'productDownloads' && <p className='text-lg font-semibold text-secondary'>{dashboardLabel?.aboutSoftware}</p>}
                {name && version && isMessageVisible ? (
                    <div className='mt-10 space-y-4'>
                        <div className='p-3 mb-2 rounded bg-sky border-primary' role='alert'>
                            <div className='flex items-center justify-between gap-2'>
                                <p className='m-0 text-md font-medium text-left text-primary'>{dashboardLabel?.latestVersion}</p>
                                <Icon
                                    icon='radix-icons:cross-2'
                                    className='cursor-pointer text-primary'
                                    width='20'
                                    height='20'
                                    onClick={() => {
                                        setIsMessageVisible(false)
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}

                {mainTab === 'productDownloads' && (
                    <>
                        <div className='inline-flex my-8 border rounded-full border-primary' role='group'>
                            <button
                                onClick={() => {
                                    showTab(0)
                                    setColumnFilters([
                                        { id: 'ItemName', value: name },
                                        { id: 'ItemVersion', value: version }
                                    ])
                                }}
                                disabled={loadingTabType === 'Prior'}
                                className={`tab-button px-7 py-3 text-xl font-semibold rounded-full ${
                                    loadingTabType === 'Prior' ? 'cursor-not-allowed opacity-50' : ''
                                } ${activeTab === 0 ? 'active-tab' : 'text-primary'}`}
                            >
                                {dashboardLabel?.currentReleases}
                            </button>
                            <button
                                onClick={() => {
                                    showTab(1)
                                    setColumnFilters([{ id: 'ItemName', value: name }])
                                }}
                                disabled={loadingTabType === 'Current'}
                                className={`tab-button px-7 py-3 text-xl font-semibold rounded-full ${
                                    loadingTabType === 'Current' ? 'cursor-not-allowed opacity-50' : ''
                                } ${activeTab === 1 ? 'active-tab' : 'text-primary'}`}
                            >
                                {dashboardLabel?.priorReleases}
                            </button>
                        </div>
                        <div>
                            {columnFilters !== null && (
                                <div className={`tab-pane`}>
                                    <ProductsTable
                                        key={productsTableKey}
                                        type={state}
                                        columnFilters={columnFilters}
                                        setColumnFilters={setColumnFilters}
                                        onLoadingChange={handleLoadingChange}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
                {mainTab === 'myLicenses' && (
                    <div>
                        {columnFilters !== null && (
                            <div className={`tab-pane`}>
                                <LicenseTable
                                    type={state}
                                    columnFilters={columnFilters}
                                    setColumnFilters={setColumnFilters}
                                    mainTab={mainTab}
                                    onLoadingChange={handleLoadingChange}
                                />
                            </div>
                        )}
                    </div>
                )}
                {/* Commercial tab content */}
                {mainTab === 'commercial' &&
                    (effectiveUserType === commonLabelName?.commercial || (isTrainingRoute && !isStudentUser && isUsa)) && (
                        <div>
                            <CommercialLicenses
                                selectedLicenses={selectedLicenses}
                                setSelectedLicenses={setSelectedLicenses}
                                quantities={quantities}
                                setQuantities={setQuantities}
                                selectedLicensesDetails={selectedLicensesDetails}
                                setSelectedLicensesDetails={setSelectedLicensesDetails}
                                setModalOpen={setModalOpen}
                                isModalOpen={isModalOpen}
                                setMainTab={setMainTab}
                                onLoadingChange={handleLoadingChange}
                            />
                        </div>
                    )}
                {/* Academic tab content */}
                {mainTab === 'academic' &&
                    (effectiveUserType === commonLabelName?.academic || (isTrainingRoute && isStudentUser && isUsa)) && (
                        <div>
                            <StudentLicenses
                                selectedLicenses={selectedLicenses}
                                setSelectedLicenses={setSelectedLicenses}
                                quantities={quantities}
                                setQuantities={setQuantities}
                                selectedLicensesDetails={selectedLicensesDetails}
                                setSelectedLicensesDetails={setSelectedLicensesDetails}
                                setModalOpen={setModalOpen}
                                isModalOpen={isModalOpen}
                                setMainTab={setMainTab}
                                onLoadingChange={handleLoadingChange}
                            />
                        </div>
                    )}
                {/* Training Courses - Commercial */}
                {mainTab === 'training' &&
                    (effectiveUserType === commonLabelName?.commercial ||
                        (isTrainingRoute && !isStudentUser && isUsa) ||
                        (mode === 'training' && effectiveUserType === commonLabelName?.commercial) ||
                        (isTrainingRoute && !stateType && !isStudentUser && isUsa) ||
                        (mode === 'training' && !stateType && !isStudentUser && isUsa)) && (
                        <div>
                            <CommercialTrainingCourses
                                selectedLicenses={selectedLicenses}
                                setSelectedLicenses={setSelectedLicenses}
                                quantities={quantities}
                                setQuantities={setQuantities}
                                selectedLicensesDetails={selectedLicensesDetails}
                                setSelectedLicensesDetails={setSelectedLicensesDetails}
                                setModalOpen={setModalOpen}
                                isModalOpen={isModalOpen}
                                setMainTab={setMainTab}
                                onLoadingChange={handleLoadingChange}
                            />
                        </div>
                    )}
                {/* Training Courses - Academic */}
                {mainTab === 'training' &&
                    (effectiveUserType === commonLabelName?.academic ||
                        (isTrainingRoute && isStudentUser && isUsa) ||
                        (mode === 'training' && effectiveUserType === commonLabelName?.academic) ||
                        (isTrainingRoute && !stateType && isStudentUser && isUsa) ||
                        (mode === 'training' && !stateType && isStudentUser && isUsa)) && (
                        <div>
                            <AcademicTrainingCourses
                                selectedLicenses={selectedLicenses}
                                setSelectedLicenses={setSelectedLicenses}
                                quantities={quantities}
                                setQuantities={setQuantities}
                                selectedLicensesDetails={selectedLicensesDetails}
                                setSelectedLicensesDetails={setSelectedLicensesDetails}
                                setModalOpen={setModalOpen}
                                isModalOpen={isModalOpen}
                                setMainTab={setMainTab}
                                onLoadingChange={handleLoadingChange}
                            />
                        </div>
                    )}
                {/* Evaluation Licenses tab content */}
                {mainTab === 'evaluation' && (
                    <div>
                        <EvaluationLicenses onLoadingChange={handleLoadingChange} />
                    </div>
                )}
                {mainTab === 'productDownloads' || 'myLicenses' ? (
                    <div className='mt-4 text-center'>
                        <h1 className='mb-3 text-lg font-semibold text-secondary'>{dashboardLabel?.queriesMessage}</h1>
                        <a target='blank' href={pathName?.contactUs} className='mt-4 text-xl font-semibold underline text-primary'>
                            {commonLabelName?.contactUs}
                        </a>
                    </div>
                ) : (
                    ''
                )}
            </div>
        </>
    )
}

export default Dashboard
